/* eslint-disable custom/to-have-test-file */

import UserAgent from '@common/UserAgent/UserAgent'

import { is } from '@common/is/is'
import { wait } from '@common/utils/timing'
import { Query } from '@common/Query/Query'

import DOM from '@common/DOM/DOM'

import getWindow from '@apps/postMessageHandler/utils/getWindow'
import postMessageHandler from '@apps/postMessageHandler/postMessageHandler'

import type {
  ILocation, TModuleType, TLevelOptions,
  IStatusResponse, IAppShellLoadedData,
  IBaseBreadCrumbLevelPage, ILevelOptionCombinationSelector
} from './definitions'

import {
  MODULAR_ATTR, ELoadingEvent, MODULE_ID_ATTR, MODULE_TYPE_ATTR,
  APPSHELL_LOADED_EVENT, ALLOWED_MODULES_TYPES, EAppPostMessageCommand
} from './definitions'

import initGlobalErrorHandlers from './globalErrorHandling/initGlobalErrorHandlers'
import checkRestricted from './checkRestricted'
import globals from './globals'

import getEduGamePage from './utils/getEduGamePage'

import './kernel'

// --- TYPE DECLARATIONS

declare const __ENGINE_ENTRY__: string
declare const __APP__: string

// --- ERROR HANDLING

// A globális errorkezelés fontos, ezért lehetőleg elsőnek fusson meg.
initGlobalErrorHandlers()

// --- CONSTANTS

const LOCATION: ILocation = {
  protocol: window.location.protocol,
  hostname: window.location.hostname,
  port: window.location.port,
  pathname: window.location.pathname,
  query: Query.decode(window.location.search)
}

/** Valamelyik modult szeretnénk indítani? */
const HAS_MODULE_ID = is.number(LOCATION.query.moduleId)

/**
 * Milyen modulfajtát kell indítani?
 * Itt van legacy kezelés, mert ha van modul azonosító, de nincs megadva a típusa, akkor az biztos 'game'.
 * @example
 * 'level' → pályaválasztó
 * 'game' → valamelyik játékmód
 *  null → nem modulos indításról van szó
 */
const MODULE_TYPE: TModuleType = is.includes(ALLOWED_MODULES_TYPES, LOCATION.query.moduleType)
  ? LOCATION.query.moduleType
  : HAS_MODULE_ID
    ? 'game'
    : null

/** Az app modulárisan lett elindítva? */
const IS_MODULAR = HAS_MODULE_ID || !is.null(MODULE_TYPE)

const TRANSITION_DURATION_PROD = 1000
const TRANSITION_DURATION_DEV  = 500

const TRANSITION_DURATION = globals.isProd
  ? TRANSITION_DURATION_PROD
  : TRANSITION_DURATION_DEV

const IS_IN_IFRAME = DOM.isInIframe()

/** Moduláris indítás és iframe beágyazás együtt csak Csillagsulinál van. */
const IS_CSILLAGSULI = IS_MODULAR && IS_IN_IFRAME

/** EDU engine-el rendelkező app? */
const IS_EDU = globals.engine === 'edu'

/** IDOCTUM engine-el rendelkező app? */
const IS_IDOCTUM = globals.engine === 'idoctum'

/** V2 engine-el rendelkező app? */
const IS_V2 = globals.engine === 'v2'

/** V2 engine alapú app? */
const IS_V2_BASED = IS_V2 || IS_EDU

/** Ha V2 appról van szó, ott legacy page-ek vannak, az újaknál már scene. */
const SCENE_OR_PAGE = IS_V2_BASED ? 'page' : 'scene'

// --- MAIN

;(async function IIFE () {
// Ezt nem kell awaitelni, jön, amikor jön
import(
  /* webpackChunkName: "sendStats" */
  './sendStats'
)
  .then(({ default: sendStats }) => {
    sendStats(UserAgent.getNavigatorUserAgent())
  })

// 4. Nem kell dinamikusan tölteni a restrictet, mert ezzel kezdünk.
const isRestricted = await checkRestricted()

// Ha ki vagyunk tiltva, akkor ennyi volt.
if (isRestricted) {
  return
}

const root = document.querySelector('#root')

if (IS_MODULAR) {
  const html = document.querySelector('html')

  // Ez alapján fogja a játék tudni, hogy speciális működésről van szó.
  html?.setAttribute(MODULAR_ATTR, '')

  html?.setAttribute(MODULE_TYPE_ATTR, MODULE_TYPE as string)

  // Ezt runTimeError esetén küldjük fel a statServicenek.
  html?.setAttribute(MODULE_ID_ATTR, `${ LOCATION.query.moduleId }`)

  const rootEl = root as HTMLElement

  // Erre azért van szükség, mert ha a GamePage opacitása 0,
  // akkor ne a szürke/fekete hátteret lássuk a userGuide modal mögött.
  rootEl.style.background = 'var(--background)'
}

let status: IStatusResponse | null = null

// Electron build.
if (
  globals.isElectron ||
  // VAGY fejleszteni szeretnénk az aktivátort, mert önmagában így nem lehet.
  (globals.isDev && globals.isActivator)
) {
  // Kell csekkolni az Activatornak.
  if (globals.isActivator) {
    const { default: initActivator } = await import(
      /* webpackChunkName: "activator" */
      './activator/activator'
    )

    const isDemo = await initActivator()

    status = {
      isDemo,
      isBlocked: false,
      source: ''
    }
  }
  // Ha nincs activator, akkor nem lesz blokkolva,
  // de a demoStatus-t a cfg-ből állíthatjuk.
  else {
    status = {
      isDemo: globals.demoStatus === 'demo',
      isBlocked: false,
      source: ''
    }
  }
}
// Cordova build.
else if (globals.isCordova) {
  status = {
    isDemo: globals.demoStatus === 'demo',
    isBlocked: false,
    source: ''
  }
}
// Webes build.
else {
  if (
    (MODULE_TYPE === 'game' || is.null(MODULE_TYPE)) &&
    globals.engine !== 'presentation'
  ) {
    // Innentől minden lépés dinamikus, mert lehet szükségtelen, az előző lépéstől függően.
    const { default: getStatus } = await import(
      /* webpackChunkName: "getStatus" */
      './ajax/calls/getStatus'
    )

    // Ha teljes verzióban indul, bárhonnan is indítják, akkor nem kell státuszt kérdezni.
    if (globals.demoStatus === 'full') {
      status = {
        isDemo: false,
        isBlocked: false,
        source: ''
      }
    }
    else {
      status = await getStatus(LOCATION)

      // Ha azt mondja a szerver MODULOS indításra, hogy nem indulhat el, akkor valamit elrontottunk.
      // Vagy nem sikerült a szervernek beazonosítani a felhasználót a küldött adatok alapján,
      // vagy valami hibát dobott a backend és fallbackkel működünk.
      if (MODULE_TYPE === 'game' && (status.isDemo || status.isBlocked)) {
        const msg = [
          'Nem sikerült a modulos indítás!',
          `/api/check-status/${ globals.serial }`
        ].join(' ')

        throw new Error(msg)
      }
    }
  }
  // MODULT_TYPE === 'level', vagy ENGINE === 'presentation'
  else {
    status = {
      isDemo: false,
      isBlocked: false,
      source: ''
    }
  }

  if (status.isBlocked) {
    const { default: blockedTemplate } = await import(
      /* webpackChunkName: "blocked" */
      './templates/blocked.pug'
    )

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { innerHTML } = document.body

    document.body.innerHTML = innerHTML + blockedTemplate({})

    return
  }
}

let moduleOptions: TLevelOptions = []
let assetExclusions: string[]    = []

// Ha szükség van a szintopciókra, akkor már a preloader előtt elkérjük.
if (IS_MODULAR && MODULE_TYPE === 'game') {
  const { default: moduleInitializer } = await import(
    /* webpackChunkName: "moduleInitializer" */
    './ajax/calls/moduleInitializer'
  )

  const moduleId = LOCATION.query.moduleId as number

  const moduleInit = await moduleInitializer(moduleId)

  if (is.null(moduleInit)) {
    throw new Error('Nem kaptunk szintopciókat a modul azonosítóhoz.')
  }

  if (is.empty(moduleInit.levelOptionsToInclude)) {
    throw new Error('Nincsenek szintopciók a modul azonosítóhoz.')
  }

  moduleOptions = moduleInit.levelOptionsToInclude
  assetExclusions = [ ...moduleInit.levelOptionsToSkip ]
}

// --------------------------------------
// ----------- BEJUTOTTUNK --------------
//              PRELOADER
// --------------------------------------

// 6. Preloader
const { default: preloader } = await import(
  /* webpackChunkName: "preloader" */
  './preloader'
)

if (IS_CSILLAGSULI) {
  const moduleId = IS_MODULAR ? LOCATION.query.moduleId : null

  postMessageHandler.emit(EAppPostMessageCommand.Preloading, moduleId, getWindow())
}

const preloaderResult = await preloader(assetExclusions)

// TODO: @ss-problem – erre majd ki kell találnunk valamit
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const w = window as any
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
w.preloaderResult = preloaderResult

// --- A preloader után majd átfadelünk a főoldalra.

let fader: HTMLElement | null = null

fader = document.createElement('div')

fader.style.top        = '0'
fader.style.left       = '0'
fader.style.width      = '100%'
fader.style.height     = '100%'
fader.style.zIndex     = '100'
fader.style.position   = 'absolute'
fader.style.transition = `opacity ${ TRANSITION_DURATION }ms`
fader.style.background = 'var(--background)'

document.body.appendChild(fader)

const { default: setupTranslations } = await import(
  /* webpackChunkName: "setupTranslations" */
  './translations/setupTranslations'
)

setupTranslations()

// Ekkor már elkezd elfadelni a preloader, lehet használni a scale-t.
// A lelke az egésznek: 16:9-es képarányt tartjuk és növeljük-csökkentjük a contentet,
// így nem kell responsive design és mindenen IS tud menni.
const { default: ContentScale } = await import(
  /* webpackChunkName: "ContentScale" */
  '@common/ContentScale/ContentScale'
)

ContentScale.init()

if (globals.engine !== 'presentation') {
  const { default: setupLanguage } = await import(
    /* webpackChunkName: "setupLanguage" */
    './setupLanguage'
  )

  setupLanguage(LOCATION)
}

// Az engine nem kerülhet be a globals-ba, mert ebben az esetben
// minden lehetőséget „behúz”, így viszont az engine konkrét értéke lesz ott.
// A CSS (main.css, vagy idoctum.css) import bekerült az engines ts fájljaiba, a már fent említett probléma miatt.
await import(
  /* webpackChunkName: "engine" */
  `./engines/${ __ENGINE_ENTRY__ }`
)

await import(
  /* webpackChunkName: "app" */
  __APP__
)

// Fejlesztés esetén a demoStatust mindenféleképp beállítjuk.
if (globals.isDev && globals.demoStatus) {
  status = { ...status, isDemo: globals.demoStatus === 'demo' }
}

const detail: IAppShellLoadedData = {
  status,
  preloaderResult,
  location: LOCATION
}

DOM.dispatch(document, APPSHELL_LOADED_EVENT, false, detail)

/** Fader kianimálása. */
function hideFader (): void {
  if (is.null(fader)) {
    return
  }

  fader.style.opacity = '0'

  // !!! FONTOS, hogy ne transitionend-re történjen meg a remove,
  // !!! mert ha elnavigálunk az oldalról a transition közben,
  // !!! akkor a transitionend-re kötött callback nem fut meg,
  // !!! és kint marad a fader, ami klikkelhetetlenné teszi az appot!
  // eslint-disable-next-line no-restricted-syntax -- Itt muszáj
  wait(TRANSITION_DURATION).then(() => {
    DOM.remove(fader)
  })
}

// Ha nem Csillagsuliban fut az app, akkor a default működést megtartjuk,
// és kianimáljuk már itt a Fadert.
if (!IS_CSILLAGSULI) {
  hideFader()
}

// Miután minden betöltődött, akkor tesszük loadedre, ekkor lekerül az app egyedi háttérszíne és a default lesz ott.
document.body.classList.add('loaded')

// Module indítás
if (IS_MODULAR) {
  const frag = document.createDocumentFragment()

  if (MODULE_TYPE === 'game') {
    const needToAppendLoadingScreen = !IS_EDU && globals.needLoadingScreen

    if (needToAppendLoadingScreen) {
      // Ha szükség van loadingScreen-re, akkor itt kerül be.
      // !!! Csak moduláris indításánál kezeljük itt a Loading-ot,
      // egyébként a SceneManager teszi meg. !!!
      const { default: loadingEl } = await import(
        /* webpackChunkName: "LoadingScreen" */
        '@apps/baseComponents/v3/Loading/Loading'
      )

      const loadingElement = loadingEl({ isVisible: true })

      loadingElement.style.zIndex = '999'

      root?.appendChild(loadingElement)
    }

    // ha EDU-s appról van szó.
    if (IS_EDU) {
      const gamePage = getEduGamePage(moduleOptions)

      frag.appendChild(gamePage)
    }
    else {
      const component = document.createElement(`sni-${ MODULE_TYPE }-${ SCENE_OR_PAGE }`)

      moduleOptions.forEach((option) => {
        component.setAttribute(option.optionKey, option.optionValue)
      })

      // Speciális tartalom, a v3-as BaseSceneContent-et használjuk a leszármazottakhoz.
      const sceneContent = document.createElement(`sni-${ SCENE_OR_PAGE }-content`)

      // Ha v2-es appról van szó, akkor a z-index-et 1-re állítjuk,
      // mert a v2-es appoknál a PageManager (bc/v2/PageManager),
      // az éppen aktuálisan látható page-et z-index 1-re állítja.
      if (IS_V2) {
        component.style.zIndex = '1'
      }

      frag.appendChild(sceneContent)
      frag.appendChild(component)
    }
  }
  else if (IS_EDU) {
    const { default: getLevelOptionCombinationsBySerial } = await import(
      /* webpackChunkName: "getLevelOptionCombinationsBySerial" */
      './ajax/calls/getLevelOptionCombinationsBySerial'
    )

    const levelOptionCombinations = await getLevelOptionCombinationsBySerial(LOCATION, globals.serial)

    await import(
       /* webpackChunkName: "LevelPagePlayButton" */
      '@apps/baseComponents/v2/LevelPagePlayButton/LevelPagePlayButton'
    )

    await import(
      /* webpackChunkName: "LevelOptionCombinationSelector" */
      '@apps/baseComponents/edu/LevelOptionCombinationSelector/LevelOptionCombinationSelector'
    )

    const component = document.createElement('edu-level-option-combination-selector')

    frag.appendChild(component)

    const selectorEl = component as unknown as ILevelOptionCombinationSelector

    selectorEl.setCombinations(levelOptionCombinations?.combinations ?? null)
  }
  else {
    const { default: getActiveLevelOptionsBySerial } = await import(
      /* webpackChunkName: "getActiveLevelOptionsBySerial" */
      './ajax/calls/getActiveLevelOptionsBySerial'
    )

    const activeLevelOptions = await getActiveLevelOptionsBySerial(LOCATION, globals.serial)

    log('[MAIN] TODO: csak az engedélyezett pályaválasztó elemeket láthassa', activeLevelOptions)

    const component = document.createElement(`sni-${ MODULE_TYPE }-${ SCENE_OR_PAGE }`)
    frag.appendChild(component)

    const bclp = component as unknown as IBaseBreadCrumbLevelPage
    bclp.setActiveLevelOptions(activeLevelOptions?.levelOptions ?? null)
  }

  root?.appendChild(frag)

  DOM.dispatch(document, 'PageManager:Game')
  DOM.dispatch(document, ELoadingEvent.Hide, false, true)

  if (IS_IN_IFRAME) {
    postMessageHandler.emit(EAppPostMessageCommand.GameStarted, undefined, getWindow())

    // A Csillagsuliban futó appoknál ITT dobjuk ki a fadert, MERT
    // a lassabb gépeken a fader kianimálása után egy villanás látható...
    hideFader()
  }

  return
}
else if (IS_EDU) {
  const mainPage = document.createElement('edu-main-page')

  root?.appendChild(mainPage)

  return
}

// Ha semmi extra nincs az indításban vagy valahol hiba történt, a végén ide csorgunk és elindul az app.

const prefix = IS_IDOCTUM
  ? 'idoc'
  : 'sni'

const managerTagName = `${ prefix }-${ SCENE_OR_PAGE }-manager`
const sceneManager = document.createElement(managerTagName)

root?.appendChild(sceneManager)
})()
